<script setup lang="ts">
import { useHasMapContext } from '@/composables/scopedStores/map/useMap'
import { useDetectBreakpoint } from '@/composables/useDetectBreakpoint'
import { computed } from 'vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    /** What variant of the banner should be used? */
    variant?: keyof typeof variantMapping
    /** Optional accessible label for the banner region */
    ariaLabel?: string
    /** Should the banner sit in flow below the nav on mobile, pushing content down? */
    fullBleedOnMobile?: boolean
    /** Is the banner showing? Kept mounted when hidden so the live region persists. */
    show?: boolean
  }>(),
  {
    variant: 'static',
    show: true
  }
)

const hasMapContext = useHasMapContext()
const isMobile = computed(() => useDetectBreakpoint('xs'))
const inFlow = computed(
  () => props.fullBleedOnMobile && hasMapContext && isMobile.value
)

const variantMapping = {
  static: 'top-[72px] right-[10px]',
  flash: 'top-[72px] right-[10px] sm:bottom-auto',
  flashHome: 'top-[72px] right-[10px]'
}
</script>

<template>
  <Teleport defer to="#mobile-banner-slot" :disabled="!inFlow">
    <div
      v-show="show"
      v-bind="$attrs"
      class="pointer-events-none"
      :class="
        inFlow
          ? 'w-full'
          : [
              'absolute left-[10px] sm:top-[72px] sm:left-0 sm:right-0',
              variantMapping[variant]
            ]
      "
      :style="{
        zIndex: inFlow ? undefined : variant === 'flash' ? 100 : 20
      }"
      :aria-label="ariaLabel"
      aria-live="assertive"
      aria-atomic="true"
    >
      <slot></slot>
    </div>
  </Teleport>
</template>
